import { useState, useCallback, useEffect } from 'react';
import PointsService, { type MemberBadge } from '../services/pointsService';
import { logger } from '../utils/logger';
interface BadgeNotification {
  badge: MemberBadge;
  show: boolean;
}

export const useBadges = () => {
  const [badgeNotification, setBadgeNotification] = useState<BadgeNotification | null>(null);
  const [badgeQueue, setBadgeQueue] = useState<MemberBadge[]>([]);
  const pointsService = PointsService.getInstance();

  const registerBadgeAsShown = useCallback((badge: MemberBadge) => {
    pointsService
      .markBadgeNotificationAsShown(badge.id)
      .catch(error => {
        console.error('❌ [useBadges] Erro ao registrar badge exibido:', error);
      });
  }, [pointsService]);

  const enqueueBadges = useCallback((badges: MemberBadge[]) => {
    if (badges.length === 0) return;

    setBadgeQueue(prevQueue => {
      const existingIds = new Set(prevQueue.map(item => item.id));
      const nextQueue = [...prevQueue];
      let hasChanges = false;

      badges.forEach(badge => {
        if (!existingIds.has(badge.id)) {
          nextQueue.push(badge);
          existingIds.add(badge.id);
          hasChanges = true;
        }
      });

      return hasChanges ? nextQueue : prevQueue;
    });
  }, []);

  const showBadge = useCallback((badge: MemberBadge) => {
    enqueueBadges([badge]);
  }, [enqueueBadges]);

  useEffect(() => {
    if (!badgeNotification && badgeQueue.length > 0) {
      const [nextBadge] = badgeQueue;
      console.log('🎯 [useBadges] Exibindo próximo badge da fila:', nextBadge.badge?.name ?? nextBadge.id);
      setBadgeNotification({
        badge: nextBadge,
        show: true
      });
    }
  }, [badgeNotification, badgeQueue]);

  const checkAndUnlockBadges = useCallback(async (
    memberId: string,
    onBadgeUnlocked?: (count: number) => void
  ): Promise<MemberBadge[]> => {
    try {
      logger.sensitive('Verificando badges', { memberId });

      const unlockedBadges = await pointsService.checkAndUnlockBadges(memberId);

      if (unlockedBadges.length === 0) {
        return [];
      }

      console.log(`🎉 [useBadges] ${unlockedBadges.length} novos badges desbloqueados!`);

      const displayableBadges = unlockedBadges.filter(badge => {
        if (badge.metadata?.retroactive) {
          return false;
        }
        return !badge.shownAt;
      });

      if (displayableBadges.length > 0) {
        console.log(`📢 [useBadges] ${displayableBadges.length} badges novos para mostrar`);
        enqueueBadges(displayableBadges);
      } else {
        console.log('ℹ️ [useBadges] Nenhum badge novo para exibir nesta verificação');
      }

      if (onBadgeUnlocked) {
        const allBadges = await pointsService.getMemberBadges(memberId);
        onBadgeUnlocked(allBadges.length);
      }

      return unlockedBadges;
    } catch (error) {
      console.error('❌ [useBadges] Erro ao verificar badges:', error);
      return [];
    }
  }, [enqueueBadges, pointsService]);

  const closeBadgeNotification = useCallback(() => {
    if (badgeNotification?.badge) {
      registerBadgeAsShown(badgeNotification.badge);
      console.log('✅ [useBadges] Badge marcado como exibido:', badgeNotification.badge.badge?.name);
    }
    setBadgeNotification(null);
    setBadgeQueue(prevQueue => prevQueue.slice(1));
  }, [badgeNotification, registerBadgeAsShown]);

  const showPendingBadges = useCallback(async (memberId: string): Promise<boolean> => {
    try {
      const unlockedBadges = await pointsService.getMemberBadges(memberId);
      const pendingBadges = unlockedBadges.filter(badge => !badge.metadata?.retroactive && !badge.shownAt);

      if (pendingBadges.length > 0) {
        console.log(`📣 [useBadges] Encontrados ${pendingBadges.length} badges pendentes para exibir`);
        enqueueBadges(pendingBadges);
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ [useBadges] Erro ao buscar badges pendentes:', error);
      return false;
    }
  }, [enqueueBadges, pointsService]);

  return {
    badgeNotification,
    badgeQueue,
    checkAndUnlockBadges,
    closeBadgeNotification,
    showBadge,
    showPendingBadges
  };
};
